import { Loader2 } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * Componente Loading state per pagine e sezioni
 * 
 * @example
 * // Full page loading
 * <PageLoader />
 * 
 * // Section loading
 * <PageLoader variant="section" text="Caricamento dati..." />
 * 
 * // Inline loading
 * <PageLoader variant="inline" size="sm" />
 */
export function PageLoader({ 
  variant = "page", 
  text = "Caricamento...",
  size = "md",
  className 
}) {
  const sizeClasses = {
    sm: "h-4 w-4",
    md: "h-8 w-8",
    lg: "h-12 w-12"
  };

  if (variant === "inline") {
    return (
      <span className={cn("inline-flex items-center gap-2", className)}>
        <Loader2 className={cn("animate-spin", sizeClasses[size])} />
        {text && <span className="text-sm text-muted-foreground">{text}</span>}
      </span>
    );
  }

  if (variant === "section") {
    return (
      <div className={cn("flex flex-col items-center justify-center py-12", className)}>
        <Loader2 className={cn("animate-spin text-primary mb-4", sizeClasses[size])} />
        {text && <p className="text-sm text-muted-foreground">{text}</p>}
      </div>
    );
  }

  // Full page loader
  return (
    <div className={cn(
      "fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm",
      className
    )}>
      <div className="flex flex-col items-center gap-4">
        <Loader2 className={cn("animate-spin text-primary", sizeClasses[size])} />
        {text && <p className="text-sm text-muted-foreground">{text}</p>}
      </div>
    </div>
  );
}

/**
 * Skeleton loader per contenuti
 */
export function Skeleton({ className, ...props }) {
  return (
    <div
      className={cn("animate-pulse rounded-md bg-muted", className)}
      {...props}
    />
  );
}

/**
 * Skeleton per card
 */
export function CardSkeleton({ className }) {
  return (
    <div className={cn("rounded-lg border bg-card p-6 space-y-4", className)}>
      <Skeleton className="h-6 w-1/3" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
    </div>
  );
}

/**
 * Skeleton per tabella
 */
export function TableSkeleton({ rows = 5, columns = 4, className }) {
  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex gap-4">
        {Array(columns).fill(0).map((_, i) => (
          <Skeleton key={i} className="h-8 flex-1" />
        ))}
      </div>
      {Array(rows).fill(0).map((_, i) => (
        <div key={i} className="flex gap-4">
          {Array(columns).fill(0).map((_, j) => (
            <Skeleton key={j} className="h-12 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

export default PageLoader;
